import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Shield, Crown, UserCog, User, Search } from "lucide-react";
import CsvImporter from "@/components/CsvImporter";

// Normaliza para pesquisa acento- e case-insensível
const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export default function TeamsAdmin() {
  const [teams, setTeams] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", recommended_consultants: 10 });
  const [search, setSearch] = useState("");

  const refresh = () => api.get("/teams").then((r) => setTeams(r.data));
  useEffect(() => { refresh(); }, []);

  // Equipas ordenadas A→Z + filtradas pela pesquisa (nome, team lead, ou qualquer membro)
  const visibleTeams = useMemo(() => {
    const sorted = [...teams].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "pt", { sensitivity: "base" })
    );
    const q = norm(search.trim());
    if (!q) return sorted;
    return sorted.filter((t) => {
      const hay = [t.name, t.team_lead?.name, ...(t.members || []).map((m) => m.name)].map(norm).join(" ");
      return hay.includes(q);
    });
  }, [teams, search]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/teams/${editing}`, form);
      else await api.post("/teams", form);
      toast.success("Equipa guardada");
      setForm({ name: "", recommended_consultants: 10 }); setEditing(null); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminar equipa? Os consultores ficam sem equipa.")) return;
    await api.delete(`/teams/${id}`); refresh();
  };

  return (
    <div className="space-y-8 kw-fade-up" data-testid="teams-page">
      <div>
        <div className="kw-overline mb-2">Administração</div>
        <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">Equipas KW</h1>
      </div>

      <form onSubmit={submit} className="kw-card p-6 grid md:grid-cols-4 gap-4 items-end" data-testid="team-form">
        <div className="md:col-span-2">
          <label className="kw-overline block mb-2">Nome da equipa</label>
          <input className="kw-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name" />
        </div>
        <div>
          <label className="kw-overline block mb-2">Nº recomendado consultores</label>
          <input type="number" min="1" className="kw-input" value={form.recommended_consultants}
            onChange={(e) => setForm({ ...form, recommended_consultants: Number(e.target.value) })} data-testid="team-rec" />
        </div>
        <div className="flex gap-2">
          <button className="kw-btn-primary inline-flex items-center gap-2" data-testid="team-submit">
            <Plus className="w-4 h-4" /> {editing ? "Guardar" : "Adicionar"}
          </button>
          {editing && (
            <button type="button" className="kw-btn-secondary" onClick={() => { setEditing(null); setForm({ name: "", recommended_consultants: 10 }); }}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </form>

      <CsvImporter
        endpoint="/import/teams"
        label="Importar equipas KW via CSV"
        sampleHeaders="name, recommended_consultants"
        onDone={refresh}
      />

      {/* Barra de pesquisa (com cada vez mais equipas torna-se essencial) */}
      <div className="kw-card p-4 flex flex-wrap items-end gap-3" data-testid="teams-search-bar">
        <div className="flex-1 min-w-[220px]">
          <label className="kw-overline block mb-1 text-[10px]">Pesquisar equipa</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
            <input
              type="text"
              className="kw-input pl-9"
              placeholder="Nome da equipa, Team Lead ou membro…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="teams-search-input"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white"
                data-testid="teams-search-clear" aria-label="Limpar pesquisa">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#737373] whitespace-nowrap pb-2.5">
          {visibleTeams.length} / {teams.length} equipa{teams.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleTeams.length === 0 && (
          <div className="text-[#737373] md:col-span-2 lg:col-span-3 kw-card p-6 text-center">
            {teams.length === 0 ? "Sem equipas. Cria a primeira acima." : "Nenhuma equipa corresponde à pesquisa."}
          </div>
        )}
        {visibleTeams.map((t) => (
          <div key={t.id} className="kw-card p-5 kw-card-hover flex flex-col" data-testid={`team-card-${t.id}`}>
            <div className="flex items-center justify-between">
              <Shield className="w-5 h-5 text-[#E50000]" />
              <div className="kw-overline">
                {t.num_consultants}/{t.recommended_consultants}
                {t.num_assistants > 0 && <span className="ml-2 text-[#FFB020]">+{t.num_assistants} ass.</span>}
              </div>
            </div>
            <div className="font-display text-2xl font-black uppercase mt-3">{t.name}</div>

            {/* Team Lead em destaque */}
            {t.team_lead ? (
              <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-[#E50000]/10 border border-[#E50000]/40" data-testid={`team-lead-${t.id}`}>
                <Crown className="w-4 h-4 text-[#E50000] flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[9px] uppercase tracking-widest font-bold text-[#E50000]">Team Lead</div>
                  <div className="text-sm font-semibold truncate">{t.team_lead.name}</div>
                </div>
              </div>
            ) : (
              <div className="mt-3 text-[10px] uppercase tracking-widest text-[#737373]">Sem Team Lead atribuído</div>
            )}

            {/* Lista de membros */}
            {t.members && t.members.length > 0 && (
              <div className="mt-3 border-t border-[#222] pt-3 flex-1">
                <div className="kw-overline mb-2 text-[9px]">Membros ({t.members.length})</div>
                <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1" data-testid={`team-members-${t.id}`}>
                  {t.members.map((m) => {
                    const isAssistant = m.role_in_team === "assistant";
                    const isBench = m.league_status === "bench";
                    const Icon = m.is_team_lead ? Crown : isAssistant ? UserCog : User;
                    const color = m.is_team_lead ? "text-[#E50000]" : isAssistant ? "text-[#FFB020]" : "text-[#A3A3A3]";
                    return (
                      <div key={m.id} className="flex items-center gap-2 text-xs" data-testid={`team-member-${m.id}`}>
                        <Icon className={`w-3 h-3 flex-shrink-0 ${color}`} />
                        <span className={`truncate ${isBench ? "text-[#666] italic" : ""}`}>{m.name}</span>
                        {m.mbti && <span className="font-mono text-[9px] text-[#737373] flex-shrink-0">{m.mbti}</span>}
                        {isAssistant && <span className="text-[8px] uppercase tracking-widest font-bold text-[#FFB020] flex-shrink-0">Ass.</span>}
                        {isBench && <span className="text-[8px] uppercase tracking-widest font-bold text-[#666] flex-shrink-0">Bench</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-4 flex justify-between border-t border-[#222] pt-3">
              <button className="text-xs uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white"
                onClick={() => { setEditing(t.id); setForm({ name: t.name, recommended_consultants: t.recommended_consultants }); }}>
                <Pencil className="w-3 h-3 inline mr-1" /> Editar
              </button>
              <button className="text-xs uppercase tracking-widest font-bold text-[#FF6B6B] hover:text-white" onClick={() => del(t.id)}>
                <Trash2 className="w-3 h-3 inline mr-1" /> Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
